import {
  getRobotIconName,
  loadAllRobotImages,
  registerRobotStyleImageMissingHandler,
} from '../../../../components/RobotOnMapEditable/robotMapUtils'
import { FeatureCollection, Point } from 'geojson'
import { useEffect, useMemo, useRef } from 'react'
import { Layer, Source, SymbolLayer, useMap } from 'react-map-gl'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { getYawFromQuaternion } from 'utils/getYawFromQuaternion'
import { IRobotInfo } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'
import { robotIconFeatureProps, robotIconOffsetFromFeature, robotIconSizeFromFeature } from 'utils/robotIconLayout'
import { VENDOR } from 'utils/vendorDisplayName'

// Individual robot icons — only shown for unclustered points
const layerRobotStyle: any = {
  id: 'robot-image-style',
  type: 'symbol',
  filter: ['!', ['has', 'point_count']],
  layout: {
    'icon-image': ['get', 'icon'],
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'map',
    'icon-size': robotIconSizeFromFeature,
    'icon-offset': robotIconOffsetFromFeature,
    'icon-rotate': ['get', 'yaw'],
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-opacity': ['get', 'opacity'],
  },
}

// Cluster circle
const layerClusterCircle: any = {
  id: 'robot-cluster-circle',
  type: 'circle',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': '#e9408f',
    'circle-radius': [
      'step',
      ['get', 'point_count'],
      20, // ≤9
      10,
      26, // 10–29
      30,
      32, // ≥30
    ],
    'circle-stroke-width': 2,
    'circle-stroke-color': 'rgba(255, 255, 255, 0.4)',
    'circle-blur': 0,
  },
}

// Cluster count label
const layerClusterCount: any = {
  id: 'robot-cluster-count',
  type: 'symbol',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': '#ffffff',
  },
}

const AllRobotsOnMap = ({
  map,
  robots,
  onRobotClicked,
  offset,
  interactive = true,
  autoFitBounds = true,
}: {
  map: any | undefined
  robots: IRobotInfo[]
  onRobotClicked: (id?: number) => void
  offset?: number
  interactive?: boolean
  autoFitBounds?: boolean
}) => {
  const maps = useMap()
  const mapFromContext = maps?.current?.getMap?.()
  const mapInstance = mapFromContext ?? map

  const robotsOnMap: FeatureCollection<Point> = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: robots
        .filter(robot => robot.lastPosition)
        .map(robot => {
          const icon = getRobotIconName(robot.vendor, robot.robot_color, robot.robot_type)

          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: robot.lastPosition?.coordinates || [],
            },
            properties: {
              opacity: robot.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE ? 1 : 0.5,
              id: robot.id,
              ...robotIconFeatureProps(
                robot.lengthM,
                robot.originToFrontM,
                robot.lastPosition?.coordinates?.[1]
              ),
              yaw:
                robot.vendor === VENDOR.ASBIS
                  ? 0
                  : robot.lastOrientation
                  ? getYawFromQuaternion(robot.lastOrientation)
                  : 0,
              icon: String(icon),
            },
          }
        }),
    }
  }, [robots])

  const hasZoomedRef = useRef(false)
  const prevAutoFitBoundsRef = useRef(autoFitBounds)

  useEffect(() => {
    if (autoFitBounds && !prevAutoFitBoundsRef.current) {
      hasZoomedRef.current = false
    }
    prevAutoFitBoundsRef.current = autoFitBounds
  }, [autoFitBounds])

  // style.load: re-add sprites after basemap/style changes (images are cleared).
  useEffect(() => {
    if (!mapInstance) return
    const onStyleLoad = () => loadAllRobotImages(mapInstance)
    mapInstance.on('style.load', onStyleLoad)
    if (mapInstance.isStyleLoaded()) {
      onStyleLoad()
    }
    return () => {
      mapInstance.off('style.load', onStyleLoad)
    }
  }, [mapInstance])

  // Mapbox GL v3: load icons on demand if the layer rendered before async addImage completed.
  useEffect(() => {
    return registerRobotStyleImageMissingHandler(mapInstance)
  }, [mapInstance])

  useEffect(() => {
    if (!autoFitBounds) {
      return
    }
    // Only zoom once when map is ready and we have robots
    if (mapInstance && robotsOnMap.features.length > 0 && !hasZoomedRef.current) {
      mapInstance.resize()
      zoomMapTo(mapInstance, robotsOnMap.features, offset ?? 600)
      hasZoomedRef.current = true
    } else if (robotsOnMap.features.length === 0) {
      hasZoomedRef.current = false
    }
  }, [mapInstance, robotsOnMap.features, offset, autoFitBounds])

  const onRobotClickedOnMap = (e: any) => {
    if (e.features[0]) {
      onRobotClicked(e.features[0].properties.id)
    } else {
      console.warn('Here should be robot, but got nothing')
    }
  }

  const onNothingClicked = (e: any) => {
    // setHighlightedRobot(e?.features && e?.features[0].properties?.id_on_map)'
    onRobotClicked()
  }

  // Zoom into cluster on click
  const onClusterClicked = (e: any) => {
    if (!mapInstance || !e.features?.[0]) return
    const feature = e.features[0]
    const clusterId = feature.properties.cluster_id
    const source = mapInstance.getSource('robot') as any
    source?.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
      if (err) return
      mapInstance.easeTo({
        center: feature.geometry.coordinates as [number, number],
        zoom,
        duration: 400,
      })
    })
  }

  useEffect(() => {
    if (!interactive || !mapInstance) {
      return
    }
    mapInstance.on('click', onNothingClicked)
    mapInstance?.on('click', 'robot-image-style', onRobotClickedOnMap)
    mapInstance?.on('click', 'robot-cluster-circle', onClusterClicked)

    // Pointer cursor on hover
    const setCursorPointer = () => {
      mapInstance.getCanvas().style.cursor = 'pointer'
    }
    const setCursorDefault = () => {
      mapInstance.getCanvas().style.cursor = ''
    }
    mapInstance?.on('mouseenter', 'robot-cluster-circle', setCursorPointer)
    mapInstance?.on('mouseleave', 'robot-cluster-circle', setCursorDefault)
    mapInstance?.on('mouseenter', 'robot-image-style', setCursorPointer)
    mapInstance?.on('mouseleave', 'robot-image-style', setCursorDefault)

    return () => {
      mapInstance.off('click', onNothingClicked)
      mapInstance.off('click', 'robot-image-style', onRobotClickedOnMap)
      mapInstance.off('click', 'robot-cluster-circle', onClusterClicked)
      mapInstance.off('mouseenter', 'robot-cluster-circle', setCursorPointer)
      mapInstance.off('mouseleave', 'robot-cluster-circle', setCursorDefault)
      mapInstance.off('mouseenter', 'robot-image-style', setCursorPointer)
      mapInstance.off('mouseleave', 'robot-image-style', setCursorDefault)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, interactive])

  return (
    <>
      {robotsOnMap && robotsOnMap.features.length > 0 && (
        <Source
          key={robotsOnMap.features.length}
          id="robot"
          type="geojson"
          data={robotsOnMap}
          cluster={true}
          clusterMaxZoom={12}
          clusterRadius={50}
        >
          <Layer {...layerClusterCircle} source="robot" />
          <Layer {...layerClusterCount} source="robot" />
          <Layer {...layerRobotStyle} source="robot" />
        </Source>
      )}
    </>
  )
}

export default AllRobotsOnMap
